import React, { useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import config from "../../../../../config";
import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const token = config.MAPBOX_ACCESS_TOKEN;

/**
 * We keep selectedPoint as [lon, lat] (same as your View page state).
 */
const MapSelectEdit = ({ selectedPoint, setSelectedPoint }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(new mapboxgl.Popup({ closeButton: true }));

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const mapStyle = darkMode
    ? "mapbox://styles/mapbox/dark-v11"
    : "mapbox://styles/mapbox/streets-v12";

  // init once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center,
      zoom,
      accessToken: token,
    });

    mapRef.current.addControl(new mapboxgl.NavigationControl());
    mapRef.current.getCanvas().style.cursor = "pointer";

    const handleClick = (e) => {
      const { lng, lat } = e.lngLat; // [lon, lat]
      setSelectedPoint?.([lng, lat]);
    };

    mapRef.current.on("click", handleClick);

    return () => {
      popupRef.current.remove();
      mapRef.current?.off("click", handleClick);
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, [center, zoom, setSelectedPoint]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(mapStyle);
  }, [darkMode]);

  // fly to region when no selection
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (selectedPoint && selectedPoint.length === 2) return;

    popupRef.current.remove();

    const fly = () => map.flyTo({ center, zoom, essential: true });

    if (map.isStyleLoaded()) fly();
    else map.once("load", fly);
  }, [center, zoom, selectedPoint]);

  // show popup when selected point changes (initial + later)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    popupRef.current.remove();
    if (!selectedPoint || selectedPoint.length !== 2) return;

    const [lng, lat] = selectedPoint;

    const show = () => {
      popupRef.current
        .setLngLat([lng, lat])
        .setHTML(`<h3>Επιλεγμένο Σημείο</h3><p>Longitude: ${lng}<br/>Latitude: ${lat}</p>`)
        .addTo(map);

      map.flyTo({ center: [lng, lat], zoom: 9, essential: true });
    };

    if (map.isStyleLoaded()) show();
    else map.once("load", show);
  }, [selectedPoint]);

  return <div ref={mapContainerRef} style={{ width: "100%", height: "100%" }} />;
};

export default MapSelectEdit;
